import {
  ActionType, DatatablesExportsInputItem, ExportStatus, ResolverType,
} from '@/types/generated-types/graphql';
import { UserState } from '@/composables/User';
import { removeAccents } from '@/helpers/helpers';
import type { StatsWidgetApiRequest } from '@/types/stats-types';
import { NestApiType, nestCall } from '@/composables/nestApi';
import { Insert } from '../GraphQL';
import type { ListSettings, SaveResult } from '../GraphQL';

const isStatsExportPayload = (value: any): value is StatsWidgetApiRequest => (
  value
  && typeof value === 'object'
  && Object.prototype.hasOwnProperty.call(value, 'widgetConfiguration')
  && Object.prototype.hasOwnProperty.call(value, 'filters')
);

const buildSaveResult = (err = '', status = false): SaveResult<DatatablesExportsInputItem> => ({
  id: 0,
  status,
  messages: [],
  err,
});

export default async function InsertDatatablesExport(idShop: number,
  settings: ListSettings|StatsWidgetApiRequest, resolverName: string, headers: Record<string, string>[], fileName: string, locale: string,
  resolverType: ResolverType, selectedIds: number[], actionType: ActionType| null, indexField: string): Promise<SaveResult<DatatablesExportsInputItem>> {
  if (resolverName === 'StatsTableGet' && isStatsExportPayload(settings)) {
    try {
      const payload = { ...settings, format: 'csv' };
      const response: any = await nestCall(
        'post',
        NestApiType.CLIENT_API,
        '/stats/export',
        { Accept: 'text/csv' },
        '',
        payload,
        true,
        {},
        { responseType: 'blob' },
      );

      if (!response) {
        return buildSaveResult('EXPORT_ERROR');
      }

      if (response instanceof Blob) {
        if (response.type && response.type.includes('application/json')) {
          const errorText = await response.text();
          try {
            const parsed = JSON.parse(errorText);
            return buildSaveResult(parsed?.errors?.[0] ?? 'EXPORT_ERROR');
          } catch (error) {
            return buildSaveResult('EXPORT_ERROR');
          }
        }

        if (!response.size) {
          return buildSaveResult('EXPORT_ERROR');
        }

        const downloadUrl = window.URL.createObjectURL(response);
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.setAttribute('download', `${removeAccents(fileName)}.csv`);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);

        return buildSaveResult('', true);
      }

      if (response.error) {
        return buildSaveResult(response.error);
      }

      return buildSaveResult('', true);
    } catch (error: any) {
      return buildSaveResult(error?.message ?? 'EXPORT_ERROR');
    }
  }

  let selectedIdsStr = null;
  if (selectedIds && selectedIds.length > 0) {
    selectedIdsStr = selectedIds.join(',');
  }

  const data: DatatablesExportsInputItem = {
    id_shop: idShop,
    filters: JSON.stringify(settings),
    resolver_name: resolverName,
    headers: JSON.stringify(headers),
    file_name: removeAccents(fileName),
    id_user: UserState.user?.id,
    user_type: UserState.user?.userType,
    locale,
    resolver_type: resolverType,
    selected_ids: selectedIdsStr,
    table_id: indexField,
    action_type: actionType,
    status: ExportStatus.Scheduled,
    date_added: new Date(),
  };

  return Insert<DatatablesExportsInputItem>(
    {
      name: 'DatatablesExports',
      input: [data],
      type: 'DatatablesExportsInput',
    },
  );
}
